import { FC, useMemo, useState } from 'react';

import {
  SendUsageGuideResult,
  sendCouponUsageGuide,
} from '@shared/api/giftCoupon/giftCoupon.controller';
import { GiftCouponSummary } from '@shared/api/giftCoupon/giftCoupon.interface';

import { SendRoleLegend } from './SendRoleLegend';

interface UsageGuideSendModalProps {
  /** 발송 대상 주문 */
  giftOrderId: string;
  /** 발송 대상 쿠폰(이미 교환 + 신청자 번호 보유) — 실제 대상/집계는 백엔드가 판단 */
  targets: GiftCouponSummary[];
  onClose: () => void;
  /** 발송 성공 후(닫기 시) 호출 */
  onSent?: () => void;
}

// ⚠️ 백엔드 기본 이용안내 문구(DEFAULT_GUIDE_MESSAGE)와 동일하게 유지할 것.
// 출처: back/smartbugo-api/src/alert/ncp-message/gift-coupon.guide.message.ts
const DEFAULT_GUIDE_PREVIEW = [
  '[답례품 이용안내]',
  '교환하신 답례품(상품권·기프티콘) 이용 시 아래 유의사항을 꼭 확인해 주세요.',
  '',
  '[유의사항]',
  '- 유효기간을 꼭 확인하신 후, 유효기간 내에 사용하셔야 합니다.',
  '- 매장에 재고가 없는 경우 동일 가격 이상의 다른 상품으로 교환되며, 차액은 고객님 부담입니다.',
  '- 표시 금액보다 낮은 가격의 상품으로 교환하면 차액(잔액)이 소멸될 수 있으니, 같거나 높은 가격의 상품으로 교환하시길 권장합니다.',
  '- 잔액 환불 등 운영방침은 사용처·브랜드마다 다르므로, 자세한 내용은 사용처에 문의해 주세요.',
].join('\n');

const onlyDigits = (raw: string): string => raw.replace(/\D/g, '');

// 010-1234-5678 형태로 가독성 있게
const formatPhone = (raw: string): string => {
  const d = onlyDigits(raw);
  if (d.length === 11) return `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
  return raw;
};

/**
 * 이용안내(상품권 교환·잔액 유의사항) 일괄 발송 모달.
 * 주문 단위로, 이미 교환했고 신청자 번호가 있는 쿠폰들에 이용안내 LMS를 한 번 더 고지한다.
 * 발송 예정 번호를 확인/제외하고, 필요 시 안내 문구를 덮어쓴 뒤 발송한다.
 */
export const UsageGuideSendModal: FC<UsageGuideSendModalProps> = ({
  giftOrderId,
  targets,
  onClose,
  onSent,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SendUsageGuideResult | null>(null);
  const [showNumbers, setShowNumbers] = useState(false);
  // 제외할 번호(숫자만) 집합 — 체크 해제한 번호는 발송하지 않는다.
  const [excludedDigits, setExcludedDigits] = useState<Set<string>>(new Set());
  // 기본 문구 대신 안내 본문을 직접 입력(덮어쓰기)할 수 있다. 비우면 기본 문구로 발송.
  const [customMode, setCustomMode] = useState(false);
  const [message, setMessage] = useState('');

  // 같은 번호가 여러 쿠폰에 걸쳐 있으면 한 줄로 묶는다(제외는 번호 단위).
  const numberGroups = useMemo(() => {
    const map = new Map<
      string,
      { digits: string; phone: string; coupons: GiftCouponSummary[] }
    >();
    for (const t of targets) {
      const phone = t.recipient?.phoneNumber ?? '';
      const digits = onlyDigits(phone);
      if (!digits) continue;
      const g = map.get(digits);
      if (g) g.coupons.push(t);
      else map.set(digits, { digits, phone, coupons: [t] });
    }
    return Array.from(map.values());
  }, [targets]);

  // 발송 예정 건수(제외 반영) = 제외되지 않은 번호의 쿠폰 수 합
  const sendCount = useMemo(
    () =>
      numberGroups.reduce(
        (acc, g) => acc + (excludedDigits.has(g.digits) ? 0 : g.coupons.length),
        0,
      ),
    [numberGroups, excludedDigits],
  );
  const excludedCount = targets.length - sendCount;

  const toggleExclude = (digits: string) => {
    setExcludedDigits((prev) => {
      const next = new Set(prev);
      if (next.has(digits)) next.delete(digits);
      else next.add(digits);
      return next;
    });
  };

  const handleSend = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const trimmed = message.trim();
      const res = await sendCouponUsageGuide({
        giftOrderId,
        message: customMode && trimmed ? trimmed : undefined,
        excludePhones: excludedDigits.size ? Array.from(excludedDigits) : undefined,
      });
      setResult(res);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : '이용안내 발송에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDone = () => {
    onSent?.();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative flex max-h-[88vh] w-full max-w-md flex-col gap-4 overflow-y-auto rounded-xl bg-white p-5 shadow-2xl">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-base font-bold text-indigo-800">
              이용안내 발송 (이미 신청한 분들께)
            </h3>
            <p className="mt-0.5 text-xs text-gray-500">
              이미 상품을 신청·받은 분들이 잘 쓸 수 있도록 교환·잔액 유의사항(이용안내)
              멘트를 LMS로 보냅니다. 아직 신청 전인 분은 대상이 아닙니다.
            </p>
            <div className="mt-2">
              <SendRoleLegend current="usageGuide" />
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            ✕
          </button>
        </div>

        {!result ? (
          <>
            {/* 발송 대상 번호 + 제외 선택 */}
            <div className="rounded-lg border border-indigo-100 bg-indigo-50/50 p-3">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-indigo-700">
                  발송 예정 {sendCount}건
                  {excludedCount > 0 && (
                    <span className="ml-1 font-normal text-gray-400">
                      · 제외 {excludedCount}건
                    </span>
                  )}
                </p>
                {numberGroups.length > 0 && (
                  <button
                    onClick={() => setShowNumbers((v) => !v)}
                    className="text-xs font-medium text-indigo-600 hover:underline"
                  >
                    {showNumbers ? '번호 숨기기' : '번호 보기'}
                  </button>
                )}
              </div>

              {showNumbers && numberGroups.length > 0 && (
                <>
                  <ul className="mt-2 max-h-48 space-y-0.5 overflow-y-auto text-xs">
                    {numberGroups.map((g) => {
                      const included = !excludedDigits.has(g.digits);
                      return (
                        <li key={g.digits}>
                          <label
                            className={`flex cursor-pointer items-center justify-between gap-2 rounded px-1.5 py-1 hover:bg-white ${
                              included ? '' : 'opacity-50'
                            }`}
                          >
                            <span className="flex min-w-0 items-center gap-2">
                              <input
                                type="checkbox"
                                checked={included}
                                onChange={() => toggleExclude(g.digits)}
                                className="h-4 w-4 shrink-0 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                              />
                              <span className="truncate text-gray-400">
                                #{g.coupons[0]?.seq}
                                {g.coupons.length > 1
                                  ? ` 외 ${g.coupons.length - 1}건`
                                  : ` · ${g.coupons[0]?.code}`}
                              </span>
                            </span>
                            <span
                              className={`shrink-0 font-medium tabular-nums ${
                                included ? 'text-gray-700' : 'text-gray-400 line-through'
                              }`}
                            >
                              {formatPhone(g.phone)}
                            </span>
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                  <p className="mt-1 text-[11px] text-gray-400">
                    체크 해제한 번호는 발송에서 제외됩니다.
                  </p>
                </>
              )}
            </div>

            {/* 기본 발송 문구 (백엔드 기본 문구 그대로) */}
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
              <p className="mb-1 text-xs font-semibold text-gray-500">
                기본 발송 문구 (제목: 답례품 이용안내)
              </p>
              <p className="whitespace-pre-wrap break-words text-xs leading-relaxed text-gray-600">
                {DEFAULT_GUIDE_PREVIEW}
              </p>
            </div>

            {/* 안내 문구 직접 입력(덮어쓰기) — 비우면 위 기본 문구로 발송 */}
            <div className="rounded-lg border border-gray-200 p-3">
              <label className="flex cursor-pointer items-center gap-2 text-xs font-medium text-gray-600">
                <input
                  type="checkbox"
                  checked={customMode}
                  onChange={(e) => setCustomMode(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                안내 문구 직접 입력 (덮어쓰기)
              </label>
              {customMode && (
                <>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={5}
                    placeholder="비워두면 위 기본 이용안내 문구로 발송됩니다."
                    className="mt-2 w-full resize-none rounded-lg border px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                  <p className="mt-1 text-[11px] text-gray-400">
                    제목 &quot;답례품 이용안내&quot;와 함께 입력하신 본문이 그대로
                    발송됩니다.
                  </p>
                </>
              )}
            </div>

            {error && (
              <p className="rounded-lg bg-red-50 px-3 py-2 text-xs font-medium text-red-700">
                {error}
              </p>
            )}

            <div className="flex justify-end gap-2">
              <button
                onClick={onClose}
                disabled={submitting}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-50"
              >
                닫기
              </button>
              <button
                onClick={handleSend}
                disabled={submitting || sendCount === 0}
                className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-bold text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {submitting ? '발송중…' : `${sendCount}건 발송`}
              </button>
            </div>
          </>
        ) : (
          /* 발송 결과 */
          <>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="rounded-lg bg-emerald-50 px-2 py-3">
                <p className="text-xs text-emerald-600">발송 성공</p>
                <p className="text-xl font-bold text-emerald-700">{result.sentCount}</p>
              </div>
              <div className="rounded-lg bg-gray-50 px-2 py-3">
                <p className="text-xs text-gray-500">건너뜀</p>
                <p className="text-xl font-bold text-gray-600">{result.skippedCount}</p>
              </div>
              <div className="rounded-lg bg-red-50 px-2 py-3">
                <p className="text-xs text-red-500">실패</p>
                <p className="text-xl font-bold text-red-600">{result.failedCount}</p>
              </div>
            </div>
            <p className="text-center text-xs text-gray-400">
              주문 내 전체 쿠폰 {result.total}건 기준
            </p>

            <div className="flex justify-end">
              <button
                onClick={handleDone}
                className="rounded-lg bg-indigo-600 px-5 py-2 text-sm font-bold text-white hover:bg-indigo-700"
              >
                확인
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
